import { LessonProblem } from '@yandex-int/k-common/typings';
import cx from 'classnames';
import { MarkdownExtXContent, Text } from 'platform-components';
import { SCREEN_RESOLUTIONS } from 'platform-components/constants';
// @ts-ignore
import * as userSelectors from 'platform-components/src/components/user/selectors';
import {
    withRouterPath,
    InjectedProps as WithRouterPathProps,
} from 'platform-components/src/utils/hocs/with-router-path/with-router-path';
import { withProblemBlockPlate } from 'platform-components/hocs';
import { getMdStateFromProblem, getProblemPreview } from 'platform-components/utils';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import ErrorBoundary from 'common.components/error-boundary/error-boundary';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { ProblemWrapper } from 'common.components/problem-wrapper/problem-wrapper';
import { TeacherProblem } from 'common.components/teacher-problem/teacher-problem';
import Usage from 'common.components/usage/usage';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import { getSubjectFromPath } from 'utils/path';

import ProblemControls from '../__problem-controls/problems__problem-controls';

import './problems__problem.scss';

interface StoreProps {
    isTeacher: boolean;
    clessonId: number | null;
    isPreparedLesson: boolean;
    shouldShowProblemUsage: boolean;
}

interface OwnProps {
    mix?: string;
    problem: LessonProblem;
    problemIndex: number;
    shouldShowTag: boolean;
    usagesCount: number | null;
    problemBlockPlateStyles: Record<string, object>;
}

interface DispatchProps {
    setSelectedProblemId: typeof problemsViewActions.setSelectedProblemId;
}

export interface Props extends OwnProps, DispatchProps, StoreProps, WithRouterPathProps {}

export const ProblemItem = (props: Props) => {
    const {
        mix,
        problem,
        problemIndex,
        problemBlockPlateStyles,
        setSelectedProblemId,
        shouldShowTag,
        isTeacher,
        usagesCount,
        clessonId,
        isPreparedLesson,
        match,
        shouldShowProblemUsage,
    } = props;
    const { problem: problemContent } = problem;
    const subject = match?.params?.subject;

    const handleProblemClick = () => {
        if (problemContent?.id) {
            setSelectedProblemId(problemContent?.id);

            ym('reachGoal', 'teacher_navigation_opened_card ', {
                problemId: problemContent?.id,
                subject,
                clessonId,
                contentType: isPreparedLesson
                    ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON
                    : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
                type: 'card',
            });
        }
    };

    // в компоненте верхнего уровня фильтруем problem без problemContent
    const instruction = getProblemPreview(problemContent!) || '';
    const mdState = getMdStateFromProblem(problemContent);

    return (
        <div className={cx('problems__problem', mix)} {...accessibleClick(handleProblemClick)}>
            {problemContent && (
                <div className="problems__problem-shield">
                    <ProblemWrapper mix="problems__problem-wrapper" problem={problemContent}>
                        <ErrorBoundary>
                            <TeacherProblem
                                imageResolution={SCREEN_RESOLUTIONS.X1}
                                previewMode={true}
                                problem={problemContent}
                                problemBlockPlateStyles={problemBlockPlateStyles}
                                readOnly={true}
                                showCorrectAnswers={false}
                                teacherMode={isTeacher}
                            />
                        </ErrorBoundary>
                    </ProblemWrapper>
                </div>
            )}
            <div className="problems__problem-info">
                {shouldShowTag && <div className="problems__problem-tag" />}
                <div className="problems__problem-instruction">
                    <Text size={Text.SIZE.S}>
                        <MarkdownExtXContent mdState={mdState}>{instruction}</MarkdownExtXContent>
                    </Text>
                </div>
                <div className="problems__problem-footer">
                    <div className="problems__problem-index">
                        <Text size={Text.SIZE.S} weight={Text.WEIGHT.M}>
                            {problemIndex + 1}
                        </Text>
                    </div>
                    <ProblemControls problem={problem} />
                </div>
            </div>
            {shouldShowProblemUsage && (
                <Usage mix="problems__problem-usage">
                    <Text id="lessonView.problems.usagesWarning" type={Text.TYPE.MARKDOWN} values={{ usagesCount }} />
                </Usage>
            )}
        </div>
    );
};

const mapStateToProps = (storeState: any, ownProps: OwnProps): StoreProps => {
    const { usagesCount } = ownProps;
    const subject = getSubjectFromPath();

    return {
        isTeacher: userSelectors.getIsTeacher(storeState),
        isPreparedLesson: lessonSelectors.getIsPreparedLesson(storeState),
        clessonId: lessonSelectors.getCLessonId(storeState),
        shouldShowProblemUsage:
            lessonViewSelectors.getCanAddProblemToLesson(storeState, { subject }) && Boolean(usagesCount),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            setSelectedProblemId: problemsViewActions.setSelectedProblemId,
        },
        dispatch
    );
};

const wrapped = withProblemBlockPlate(withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ProblemItem)));

wrapped.displayName = 'LessonViewProblem';

export default wrapped;
